import { GroupLabel } from "@/domain/organizations.interface";
import {
  GroupLabelCreateRequestPayload,
  GroupLabelUpdateRequestPayload,
  SearchGroupLabelPayload,
} from "@/domain/grouplabel.interface";
import { GroupLabelsService } from "@/services/groupLabels.service";
import { useMutation, useQuery } from "@tanstack/react-query";

export const useGetGroupLabels = () => {
  const {
    data: groupLabels,
    isLoading: isLoadingGroupLabels,
    isError: isErrorGroupLabels,
    refetch: refetchGroupLabels,
  } = useQuery<GroupLabel[]>({
    queryKey: ["coyn-group-labels-list"],
    queryFn: () => GroupLabelsService.findAll(),
  });

  return {
    groupLabels,
    isLoadingGroupLabels,
    isErrorGroupLabels,
    refetchGroupLabels,
  };
};

export const useGroupLabels = (
  {
    sortField,
    sortOrder,
    pageNumber,
    pageSize,
    search,
    status,
  }: SearchGroupLabelPayload,
  enabled = true,
) => {
  const {
    data: groupLabelsResponse,
    isLoading: isLoadingGroupLabels,
    refetch: refetchGroupLabels,
  } = useQuery({
    queryKey: [
      "coyn-grouplabels",
      sortField,
      sortOrder,
      pageNumber,
      pageSize,
      search,
      status,
    ],
    queryFn: () =>
      GroupLabelsService.search({
        sortField,
        sortOrder,
        pageNumber,
        pageSize,
        search,
        status,
      }),
    enabled,
  });

  return {
    groupLabels: groupLabelsResponse?.groupLabels,
    totalCount: groupLabelsResponse?.totalCount,
    isLoadingGroupLabels,
    refetchGroupLabels,
  };
};

export const useGroupLabelCreateOne = () => {
  return useMutation({
    mutationFn: (data: GroupLabelCreateRequestPayload) =>
      GroupLabelsService.create(data),
  });
};

export const useGroupLabelUpdateOne = () => {
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: GroupLabelUpdateRequestPayload;
    }) => {
      await GroupLabelsService.update({ id, data });
    },
  });
};

export const useGroupLabelDeleteOne = () => {
  return useMutation({
    mutationFn: (id: string) => GroupLabelsService.softDelete(id),
  });
};

export const useAllGroupLabels = (enabled = true) => {
  const {
    data: allGroupLabels,
    isLoading: isLoadingAllGroupLabels,
    refetch: refetchAllGroupLabels,
  } = useQuery({
    queryKey: ["coyn-all-grouplabels"],
    queryFn: () => GroupLabelsService.findAll(),
    enabled,
  });

  return {
    allGroupLabels,
    isLoadingAllGroupLabels,
    refetchAllGroupLabels,
  };
};
